import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Clock3, Send, CheckCircle2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fr } from "date-fns/locale";
import { SERVICES, TIME_SLOTS } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatISODate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Booking() {
  const location = useLocation();
  const presetService = location.state?.service;

  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [service, setService] = useState(presetService || SERVICES[1].title);
  const [mode, setMode] = useState("domicile");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [childAge, setChildAge] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (presetService) setService(presetService);
  }, [presetService]);

  const disabledMatcher = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (day < today) return true;
    // Sunday = 0 (closed)
    if (day.getDay() === 0) return true;
    return false;
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Merci de choisir une date et un créneau.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        name,
        email,
        phone,
        service,
        mode,
        preferred_date: formatISODate(date),
        preferred_time: time,
        child_age: childAge || null,
        notes: notes || null,
      });
      setSuccess(true);
      toast.success("Demande envoyée ! Léa vous recontacte sous 24h.");
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Réessayez ou contactez-nous directement.");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div data-testid="booking-success" className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center">
        <span className="inline-flex w-16 h-16 rounded-full bg-[#F4EBE6] items-center justify-center text-[#BA8E82] mb-8">
          <CheckCircle2 size={28} strokeWidth={1.4} />
        </span>
        <h1 className="font-serif text-5xl md:text-6xl text-[#4A403A] leading-tight mb-6">
          Merci, {name.split(" ")[0] || "à bientôt"}&nbsp;!
        </h1>
        <p className="text-[#8B7E74] text-lg leading-relaxed max-w-xl mx-auto mb-8">
          Votre demande de rendez-vous a bien été reçue. Léa vous recontacte sous 24h
          ouvrées pour confirmer votre créneau et préparer votre séance.
        </p>
        <a
          href="/"
          data-testid="booking-back-home"
          className="inline-flex items-center gap-2 rounded-full px-7 py-3 bg-[#4A403A] text-[#FAF7F2] text-sm tracking-wide transition-all hover:-translate-y-0.5"
        >
          Retour à l'accueil
        </a>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28">
      <p className="text-xs tracking-[0.25em] uppercase text-[#8B7E74] mb-6">Rendez-vous</p>
      <h1 data-testid="booking-title" className="font-serif text-5xl md:text-6xl text-[#4A403A] leading-[1.05] mb-6 max-w-3xl">
        Réservons un moment<br/><em className="text-[#BA8E82]">ensemble</em>.
      </h1>
      <p className="text-lg text-[#8B7E74] max-w-2xl leading-relaxed mb-14">
        Choisissez votre prestation, une date et un créneau qui vous convient. Léa vous recontacte
        sous 24h pour confirmer le rendez-vous et préparer votre séance.
      </p>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-12 gap-8">
        {/* Calendar */}
        <div className="lg:col-span-7 bg-white rounded-3xl p-8 border border-[#E5DCD6] shadow-softer">
          <div className="flex items-center gap-3 mb-6">
            <CalendarDays size={18} className="text-[#BA8E82]" />
            <h2 className="font-serif text-2xl text-[#4A403A]">Date souhaitée</h2>
          </div>
          <div className="flex justify-center" data-testid="booking-calendar">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={disabledMatcher}
              locale={fr}
              className="rounded-2xl"
            />
          </div>

          <div className="mt-10">
            <div className="flex items-center gap-3 mb-5">
              <Clock3 size={18} className="text-[#BA8E82]" />
              <h2 className="font-serif text-2xl text-[#4A403A]">Créneau</h2>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {TIME_SLOTS.map((t) => (
                <button
                  type="button"
                  key={t}
                  data-testid={`time-slot-${t}`}
                  onClick={() => setTime(t)}
                  className={`rounded-full py-2.5 text-sm tracking-wide border transition-all ${
                    time === t
                      ? "bg-[#4A403A] text-[#FAF7F2] border-[#4A403A]"
                      : "bg-white text-[#4A403A] border-[#E5DCD6] hover:border-[#D2A69A]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#8B7E74] mt-4">
              Horaires : lundi au samedi, 8h – 18h. Dimanche fermé.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-5 bg-white rounded-3xl p-8 border border-[#E5DCD6] shadow-softer space-y-5">
          <h2 className="font-serif text-2xl text-[#4A403A] mb-2">Vos coordonnées</h2>

          <div>
            <Label htmlFor="service" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Prestation</Label>
            <Select value={service} onValueChange={setService}>
              <SelectTrigger id="service" data-testid="booking-service-select" className="mt-2 h-12 rounded-xl border-[#E5DCD6] bg-white/60">
                <SelectValue placeholder="Choisir une prestation" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s.slug} value={s.title} data-testid={`booking-service-option-${s.slug}`}>
                    {s.title} — {s.price}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Modalité</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {[
                { v: "domicile", label: "À domicile" },
                { v: "visio", label: "En visio (−20%)" },
              ].map((m) => (
                <button
                  type="button"
                  key={m.v}
                  data-testid={`booking-mode-${m.v}`}
                  onClick={() => setMode(m.v)}
                  className={`rounded-xl py-3 text-sm tracking-wide border transition-all ${
                    mode === m.v
                      ? "bg-[#F4EBE6] text-[#4A403A] border-[#D2A69A]"
                      : "bg-white text-[#8B7E74] border-[#E5DCD6] hover:border-[#D2A69A]"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="name" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Nom complet</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="booking-name" className="mt-2 h-12 rounded-xl border-[#E5DCD6] bg-white/60" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="booking-email" className="mt-2 h-12 rounded-xl border-[#E5DCD6] bg-white/60" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Téléphone</Label>
                <Input id="phone" required value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="booking-phone" className="mt-2 h-12 rounded-xl border-[#E5DCD6] bg-white/60" />
              </div>
            </div>
            <div>
              <Label htmlFor="childAge" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Âge de l'enfant</Label>
              <Input id="childAge" placeholder="Ex. 18 mois" value={childAge} onChange={(e) => setChildAge(e.target.value)} data-testid="booking-child-age" className="mt-2 h-12 rounded-xl border-[#E5DCD6] bg-white/60" />
            </div>
            <div>
              <Label htmlFor="notes" className="text-xs tracking-[0.15em] uppercase text-[#8B7E74]">Quelques mots sur votre situation</Label>
              <Textarea id="notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="booking-notes" className="mt-2 rounded-xl border-[#E5DCD6] bg-white/60" />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="booking-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 bg-[#4A403A] text-[#FAF7F2] text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#3a322d] disabled:opacity-60"
          >
            {submitting ? "Envoi en cours…" : (<>Envoyer ma demande <Send size={15} /></>)}
          </button>

          <p className="text-xs text-[#8B7E74] text-center">
            Votre demande sera confirmée par retour de mail ou téléphone sous 24h.
          </p>
        </div>
      </form>
    </div>
  );
}
